import React from 'react';
import styled from 'styled-components';
import {
  FileText,
  Target,
  Award,
  CheckCircle,
  Calendar,
  BarChart3,
  ArrowRight,
  ClipboardCheck,
  Edit3,
  AlertTriangle
} from 'lucide-react';
import { theme } from '../styles/theme';
import { FinalReport } from '../types';

const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  height: 100%;
  max-height: 100%;
  padding: ${theme.spacing[4]};
  background: linear-gradient(135deg, 
    #ffffff 0%, 
    #f8f9fa 35%, 
    #f1f3f4 100%
  );
  color: ${theme.colors.textPrimary};
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  border-bottom: 1px solid ${theme.colors.border};
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: ${theme.borderRadius.full};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary[300]};
    border-radius: ${theme.borderRadius.full};
    transition: background ${theme.transitions.base};
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.primary[400]};
  }
  
  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: ${theme.colors.primary[300]} rgba(255, 255, 255, 0.1);
  
  /* Subtle animated background pattern for white theme */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(147, 51, 234, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(16, 185, 129, 0.05) 0%, transparent 50%);
    pointer-events: none;
    animation: pulseGlow 8s ease-in-out infinite alternate;
    z-index: 0;
  }
  
  /* All content above the animated background */
  > * {
    position: relative;
    z-index: 1;
  }
  
  @keyframes pulseGlow {
    0% {
      opacity: 0.3;
      transform: scale(1);
    }
    100% {
      opacity: 0.6;
      transform: scale(1.05);
    }
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  background: rgba(255, 255, 255, 0.8);
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border};
  backdrop-filter: blur(20px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
`;

const ReportTitle = styled.h1`
  margin: 0 0 ${theme.spacing[2]} 0;
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
`;

const TitleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[500]});
  border-radius: ${theme.borderRadius.md};
  backdrop-filter: blur(20px);
  border: 1px solid ${theme.colors.primary[300]};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all ${theme.transitions.base};
  color: white;
  
  &:hover {
    transform: translateY(-1px) scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const DateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: linear-gradient(135deg, ${theme.colors.primary[100]}, ${theme.colors.primary[50]});
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  border: 1px solid ${theme.colors.primary[200]};
  backdrop-filter: blur(15px);
  font-weight: ${theme.typography.fontWeight.medium};
  letter-spacing: 0.3px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  color: ${theme.colors.primary[700]};
`;

const ScoreSection = styled.div`
  margin: ${theme.spacing[3]} 0;
  text-align: center;
  background: rgba(255, 255, 255, 0.8);
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border};
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: conic-gradient(from 0deg, transparent, ${theme.colors.primary[500]}10, transparent);
    animation: rotate 10s linear infinite;
    pointer-events: none;
  }
  
  @keyframes rotate {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ScoreCircle = styled.div<{ score: number | null; maxScore: number }>`
  width: 100px;
  height: 100px;
  border-radius: ${theme.borderRadius.full};
  /* Phase A — when score is null we render a flat ring (no fabricated arc).
     The text inside reads "—" so the user can tell scoring is pending,
     not zero. */
  background: ${({ score, maxScore }) => {
    if (typeof score !== 'number') {
      return `radial-gradient(circle, rgba(148, 163, 184, 0.18) 60%, rgba(148, 163, 184, 0.32) 60%)`;
    }
    const deg = Math.max(0, Math.min(360, (score / maxScore) * 360));
    return `conic-gradient(from 0deg, rgba(34, 197, 94, 0.75) 0deg ${deg}deg, rgba(34, 197, 94, 0.06) ${deg}deg 360deg)`;
  }};

  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing[2]} auto;
  position: relative;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2));
  transition: all ${theme.transitions.base};
  
  &:hover {
    transform: scale(1.05);
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.25));
  }

  &::before {
    content: '';
    position: absolute;
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, ${theme.colors.primary[700]}, ${theme.colors.accent[700]});
    border-radius: ${theme.borderRadius.full};
    box-shadow: inset 0 1px 4px rgba(0, 0, 0, 0.1);
  }
`;

const ScoreContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;

  .score {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    margin: 0;
    color: white;
  }

  .max-score {
    font-size: ${theme.typography.fontSize.base};
    opacity: 0.8;
    margin: 0;
    color: white;
  }
`;

const ScoreLabel = styled.div`
  font-size: ${theme.typography.fontSize.base};
  opacity: 0.9;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.3px;
  margin-top: ${theme.spacing[1]};
  color: ${theme.colors.textSecondary};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[2]};
  margin: ${theme.spacing[3]} 0;
  flex-shrink: 0;
  
  @media (max-width: 320px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[2]};
  }
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 249, 250, 0.9));
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[3]};
  text-align: center;
  backdrop-filter: blur(20px);
  transition: all ${theme.transitions.base};
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, ${theme.colors.primary[500]}10, transparent);
    transition: left ${theme.transitions.base};
  }

  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 1), rgba(248, 249, 250, 1));
    transform: translateY(-1px) scale(1.01);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: ${theme.colors.primary[300]};
    
    &::before {
      left: 100%;
    }
  }
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[500]});
  border-radius: ${theme.borderRadius.sm};
  margin: 0 auto ${theme.spacing[2]} auto;
  border: 1px solid ${theme.colors.primary[300]};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  transition: all ${theme.transitions.base};
  color: white;
  
  .parent:hover & {
    transform: scale(1.1) rotate(5deg);
  }
`;

const StatValue = styled.div`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: ${theme.spacing[1]};
  color: ${theme.colors.textPrimary};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  opacity: 0.8;
  font-weight: ${theme.typography.fontWeight.medium};
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: ${theme.colors.textSecondary};
`;

const CriteriaSection = styled.div`
  margin-top: ${theme.spacing[3]};
  background: rgba(255, 255, 255, 0.8);
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  backdrop-filter: blur(20px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  /* Previous styling used flex:1 + overflow:hidden, which clipped any
     criteria that didn't fit the available height — visible items
     looked like "only 3 of 5 are showing." Let the section size to its
     content instead, and rely on the outer page scroll for overflow. */
  flex-shrink: 0;
`;

const SectionHeader = styled.h2`
  margin: 0 0 ${theme.spacing[3]} 0;
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  letter-spacing: 0.2px;
  position: relative;
  flex-shrink: 0;
  color: ${theme.colors.textPrimary};
  
  svg {
    color: ${theme.colors.primary[500]};
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -${theme.spacing[1]};
    left: 0;
    width: 30px;
    height: 2px;
    background: linear-gradient(90deg, ${theme.colors.primary[500]}, transparent);
    border-radius: ${theme.borderRadius.full};
  }
`;

const CriteriaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  flex: 1;
  min-height: 0;
`;

const CriteriaItem = styled.div<{ priorityLevel: string }>`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 249, 250, 0.9));
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  backdrop-filter: blur(20px);
  position: relative;
  transition: all ${theme.transitions.base};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  flex-shrink: 0;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, 
      ${({ priorityLevel }) => {
        switch (priorityLevel) {
          case 'Pass/Fail': return '#000052, #000052';
          case 'Must': return '#000052, #000052';
          case 'Should': return '#f59e0b, #000052';
          case 'Could': return '#000052, #000052';
          case 'Cost Effectiveness': return '#000052, #000052';
          default: return `${theme.colors.textMuted}, ${theme.colors.textSecondary}`;
        }
      }}
    );
    border-radius: 0 ${theme.borderRadius.sm} ${theme.borderRadius.sm} 0;
    box-shadow: 1px 0 2px rgba(0, 0, 0, 0.05);
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 1), rgba(248, 249, 250, 1));
    transform: translateX(2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: ${theme.colors.primary[300]};
  }
`;

const CriteriaHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[1]};
  gap: ${theme.spacing[2]};
`;

const CriteriaName = styled.div`
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.tight};
  flex: 1;
  min-width: 0;
  color: ${theme.colors.textPrimary};
`;

const CriteriaWeight = styled.div<{ hasWeight: boolean }>`
  font-size: 10px;
  font-weight: ${theme.typography.fontWeight.bold};
  opacity: 0.9;
  background: ${({ hasWeight }) => 
    hasWeight 
      ? `linear-gradient(135deg, ${theme.colors.primary[100]}, ${theme.colors.primary[50]})` 
      : `linear-gradient(135deg, ${theme.colors.backgroundSecondary}, ${theme.colors.surface})`
  };
  color: ${({ hasWeight }) => 
    hasWeight ? theme.colors.primary[700] : theme.colors.textSecondary
  };
  padding: ${({ hasWeight }) => 
    hasWeight ? '4px 8px' : '4px 6px'
  };
  border-radius: ${theme.borderRadius.full};
  border: 1px solid ${({ hasWeight }) => 
    hasWeight ? theme.colors.primary[200] : theme.colors.border
  };
  backdrop-filter: blur(10px);
  white-space: nowrap;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const CriteriaDescription = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  opacity: 0.8;
  line-height: ${theme.typography.lineHeight.normal};
  color: ${theme.colors.textSecondary};
  margin-top: ${theme.spacing[1]};
  
  /* Ensure text wraps properly and is fully visible */
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
`;

const NavigationSection = styled.div`
  margin-top: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  background: rgba(255, 255, 255, 0.8);
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border};
  backdrop-filter: blur(20px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: relative;
  flex-shrink: 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${theme.colors.primary[300]}, transparent);
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 249, 250, 0.9));
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textPrimary};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  margin-bottom: ${theme.spacing[2]};
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  
  svg {
    color: ${theme.colors.primary[500]};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, ${theme.colors.primary[500]}10, transparent);
    transition: left ${theme.transitions.base};
  }

  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 1), rgba(248, 249, 250, 1));
    transform: translateX(3px) scale(1.01);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: ${theme.colors.primary[300]};
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateX(2px) scale(0.99);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const NavButtonText = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.2px;
  color: ${theme.colors.textPrimary};
`;

interface FinalReportSummaryProps {
  finalReport?: FinalReport;
  onNavigateToSection?: (section: string) => void;
  isLoading?: boolean;
  /** When true, the run was started in GENERATE mode — drafts authored from
   *  blank responses. The headline score is then "Avg draft quality" (KB
   *  grounding) instead of "Average response score" (rubric improvement). */
  isGenerateMode?: boolean;
}

const FinalReportSummary: React.FC<FinalReportSummaryProps> = ({
  finalReport,
  onNavigateToSection,
  isLoading = false,
  isGenerateMode = false,
}) => {
  // Parse evaluation criteria if it comes as a string
  const parsedCriteria = React.useMemo(() => {
    if (!finalReport?.evaluation_criteria) return [];
    
    const criteria = finalReport.evaluation_criteria as any; // Type assertion to bypass strict typing
    
    // If it's already an array, return as is
    if (Array.isArray(criteria)) {
      return criteria;
    }
    
    // If it's a string, try to parse it
    if (typeof criteria === 'string') {
      try {
        // First try direct JSON parsing
        let parsed = JSON.parse(criteria);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        try {
          // If that fails, try fixing single quotes to double quotes and parse again
          const fixedString = criteria.replace(/'/g, '"');
          let parsed = JSON.parse(fixedString);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e2) {
          console.warn('Failed to parse evaluation criteria after all attempts:', e2);
          return [];
        }
      }
    }
    
    return [];
  }, [finalReport?.evaluation_criteria]);

  if (!finalReport) {
    return null;
  }

  // Phase A — counts pulled from the structured payload. We treat refined +
  // drafted as a single "answered" denominator since both reach the bidder's
  // submission file. Excluded counts are flagged separately so the user can
  // see what they opted out of.
  const refinedCount = finalReport.refined_responses?.length || 0;
  const draftedCount = finalReport.drafted_responses?.length || 0;
  const excludedCount = finalReport.excluded_questions?.length || 0;
  const complianceCount = finalReport.compliance_items?.length || 0;
  const totalAnswered = refinedCount + draftedCount;

  // Score: trust the rubric average from buildFinalReport. null === "—"
  // (no fabricated 0/100). For the human-readable score string we render
  // either the integer score or the em-dash, never both.
  const score = finalReport.overall_score;
  const scoreText = typeof score === 'number' ? Math.round(score).toString() : '—';

  return (
    <SummaryContainer>
      <HeaderSection>
        <ReportTitle>
          <TitleIcon>
            <FileText size={24} />
          </TitleIcon>
          Final Report
        </ReportTitle>
        <DateBadge>
          <Calendar size={14} />
          {finalReport.date}
        </DateBadge>
      </HeaderSection>

      <ScoreSection>
        <ScoreCircle score={score} maxScore={finalReport.max_score}>
          <ScoreContent>
            <div className="score">{scoreText}</div>
            <div className="max-score">/{finalReport.max_score}</div>
          </ScoreContent>
        </ScoreCircle>
        <ScoreLabel>
          {typeof score === 'number'
            ? isGenerateMode
              ? 'Average draft quality'
              : 'Average response score'
            : 'Score pending'}
        </ScoreLabel>
      </ScoreSection>

      {isGenerateMode && typeof finalReport.kb_grounding_score === 'number' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing[2],
            padding: theme.spacing[3],
            background:
              finalReport.kb_grounding_score < 40
                ? theme.colors.warning[100] + '40'
                : theme.colors.primary[100] + '20',
            border: `1px solid ${
              finalReport.kb_grounding_score < 40
                ? theme.colors.warning[500]
                : theme.colors.primary[300]
            }`,
            borderRadius: theme.borderRadius.md,
          }}
          role="status"
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing[2],
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.textPrimary,
            }}
          >
            <Award size={14} />
            <span>Evidence grounding (KB)</span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.semibold,
                color:
                  finalReport.kb_grounding_score < 40
                    ? theme.colors.warning[700]
                    : theme.colors.textPrimary,
              }}
            >
              {finalReport.kb_grounding_score}/100
            </span>
          </div>
          {finalReport.kb_grounding_score < 40 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: theme.spacing[2],
                fontSize: theme.typography.fontSize.xs,
                color: theme.colors.warning[700],
                lineHeight: theme.typography.lineHeight.relaxed,
              }}
            >
              <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                Drafts were authored with limited evidence from your knowledge
                base. Review and add citations before submission, or upload more
                relevant company documents to improve grounding on the next run.
              </span>
            </div>
          )}
        </div>
      )}

      <StatsGrid>
        <StatCard className="parent">
          <StatIcon>
            <FileText size={14} />
          </StatIcon>
          <StatValue>{totalAnswered}</StatValue>
          <StatLabel>
            {isGenerateMode
              ? `Generated (${refinedCount + draftedCount} drafted)`
              : `Answered (${refinedCount} refined · ${draftedCount} drafted)`}
          </StatLabel>
        </StatCard>

        <StatCard className="parent">
          <StatIcon>
            <Target size={14} />
          </StatIcon>
          <StatValue>{parsedCriteria.length}</StatValue>
          <StatLabel>Criteria</StatLabel>
        </StatCard>

        <StatCard className="parent">
          <StatIcon>
            <ClipboardCheck size={14} />
          </StatIcon>
          <StatValue>{complianceCount}</StatValue>
          <StatLabel>Compliance items</StatLabel>
        </StatCard>

        <StatCard className="parent">
          <StatIcon>
            <Award size={14} />
          </StatIcon>
          <StatValue>
            {parsedCriteria.filter((c: any) =>
              c.priority_level === 'Must' || c.priority_level === 'Pass/Fail'
            ).length}
          </StatValue>
          <StatLabel>Critical criteria</StatLabel>
        </StatCard>
      </StatsGrid>

      {excludedCount > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing[2],
            padding: theme.spacing[2],
            margin: `${theme.spacing[2]} 0`,
            background: theme.colors.warning[100] + '60',
            border: `1px solid ${theme.colors.warning[100]}`,
            borderRadius: theme.borderRadius.md,
            fontSize: theme.typography.fontSize.xs,
            color: theme.colors.warning[700],
          }}
          role="status"
        >
          <Edit3 size={12} />
          <span>
            {excludedCount} question{excludedCount === 1 ? '' : 's'} excluded by you — not included in submission.
          </span>
        </div>
      )}

      <CriteriaSection>
        <SectionHeader>
          <CheckCircle size={20} />
          Evaluation Criteria
        </SectionHeader>
        <CriteriaList>
          {parsedCriteria.map((criterion, index) => (
            <CriteriaItem key={index} priorityLevel={criterion.priority_level || 'Should'}>
              <CriteriaHeader>
                <CriteriaName>{criterion.name}</CriteriaName>
                <CriteriaWeight hasWeight={!!criterion.weight}>
                  {criterion.weight ? `${criterion.weight}%` : (criterion.priority_level || 'Should')}
                </CriteriaWeight>
              </CriteriaHeader>
              <CriteriaDescription>
                {criterion.description || 'No description available'}
              </CriteriaDescription>
            </CriteriaItem>
          ))}
        </CriteriaList>
      </CriteriaSection>

      {/* Phase B — Evidence Grounding panel. Surfaces BOTH the positive
          grounding signal (KB excerpts cited, past-tender titles pulled,
          buyer organisations referenced) AND the warnings (drafted answers
          weakly grounded). Earlier versions only showed the warnings,
          which misrepresented runs where 50 KB chunks fed 10 refined
          responses but 1 drafted answer happened to lack citations. */}
      {finalReport.evidence_summary &&
        (finalReport.evidence_summary.total_drafted > 0 ||
          (finalReport.evidence_summary.positive_signal &&
            ((finalReport.evidence_summary.positive_signal.kb_excerpts_used || 0) +
              (finalReport.evidence_summary.positive_signal.past_tender_excerpts_used || 0) +
              (finalReport.evidence_summary.positive_signal.questions_with_past_matches || 0) >
              0))) && (
        <CriteriaSection>
          <SectionHeader>
            <BarChart3 size={20} />
            Evidence Grounding
          </SectionHeader>
          {(() => {
            const ev = finalReport.evidence_summary!;
            const ps = ev.positive_signal;
            const counts = ev.tier_counts || {};
            const TIER_TONE: Record<string, string> = {
              excellent: '#16a34a',
              good: '#22c55e',
              fair: '#eab308',
              poor: '#f97316',
              critical: '#dc2626',
            };
            const tiers: Array<keyof typeof TIER_TONE> = ['excellent', 'good', 'fair', 'poor', 'critical'];
            const groundingPillStyle = {
              display: 'inline-flex' as const,
              alignItems: 'center' as const,
              gap: 6,
              padding: '4px 10px',
              borderRadius: theme.borderRadius.full,
              fontSize: theme.typography.fontSize.xs,
              fontWeight: theme.typography.fontWeight.semibold,
              color: '#15803d',
              background: '#dcfce7',
              border: '1px solid #86efac',
            };
            return (
              <>
                {ps && (ps.kb_excerpts_used + ps.past_tender_excerpts_used + ps.questions_with_past_matches > 0) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2], marginBottom: theme.spacing[3] }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing[1] }}>
                      {ps.refined_with_kb > 0 && (
                        <span style={groundingPillStyle}>
                          {ps.refined_with_kb} refined answer{ps.refined_with_kb === 1 ? '' : 's'} grounded
                        </span>
                      )}
                      {ps.kb_excerpts_used > 0 && (
                        <span style={groundingPillStyle}>
                          {ps.kb_excerpts_used} KB excerpt{ps.kb_excerpts_used === 1 ? '' : 's'} cited
                        </span>
                      )}
                      {ps.past_tender_excerpts_used > 0 && (
                        <span style={groundingPillStyle}>
                          {ps.past_tender_excerpts_used} past-tender excerpt{ps.past_tender_excerpts_used === 1 ? '' : 's'}
                        </span>
                      )}
                      {ps.questions_with_past_matches > 0 && (
                        <span style={groundingPillStyle}>
                          {ps.questions_with_past_matches} question{ps.questions_with_past_matches === 1 ? '' : 's'} with past-tender matches
                        </span>
                      )}
                    </div>
                    {ps.top_past_tender_titles.length > 0 && (
                      <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary, lineHeight: 1.6 }}>
                        <strong style={{ color: theme.colors.textPrimary }}>Past tenders cited:</strong>{' '}
                        {ps.top_past_tender_titles.slice(0, 5).join(' · ')}
                      </div>
                    )}
                    {ps.top_buyer_organisations.length > 0 && (
                      <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary, lineHeight: 1.6 }}>
                        <strong style={{ color: theme.colors.textPrimary }}>Buyer organisations referenced:</strong>{' '}
                        {ps.top_buyer_organisations.slice(0, 5).join(' · ')}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: theme.spacing[1], flexWrap: 'wrap', marginBottom: theme.spacing[2] }}>
                  {tiers.map((t) => {
                    const n = (counts as any)[t] || 0;
                    if (!n) return null;
                    return (
                      <span
                        key={t}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '2px 10px',
                          borderRadius: theme.borderRadius.full,
                          fontSize: theme.typography.fontSize.xs,
                          fontWeight: theme.typography.fontWeight.semibold,
                          color: TIER_TONE[t],
                          background: `${TIER_TONE[t]}15`,
                          border: `1px solid ${TIER_TONE[t]}40`,
                          textTransform: 'capitalize',
                        }}
                      >
                        Drafts {t} · {n}
                      </span>
                    );
                  })}
                </div>
                {ev.flagged_question_ids.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: theme.spacing[2],
                      padding: theme.spacing[2],
                      background: '#dc262610',
                      border: `1px solid #dc262633`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: theme.typography.fontSize.xs,
                      color: '#991b1b',
                      marginBottom: theme.spacing[2],
                    }}
                  >
                    <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>
                      <strong>{ev.flagged_question_ids.length}</strong> drafted answer
                      {ev.flagged_question_ids.length === 1 ? ' is' : 's are'} weakly grounded —
                      review before submission.
                    </span>
                  </div>
                )}
                {ev.recurring_gaps.length > 0 && (
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary, lineHeight: 1.5 }}>
                    <strong style={{ color: theme.colors.textPrimary }}>Recurring KB gaps:</strong>{' '}
                    {ev.recurring_gaps.slice(0, 4).join(' · ')}
                    {ev.recurring_gaps.length > 4 ? ` +${ev.recurring_gaps.length - 4} more` : ''}
                  </div>
                )}
              </>
            );
          })()}
        </CriteriaSection>
      )}

      {/* Phase B — Per-criterion roll-up. Shows where the bid is strong and
          where it's thin. Only renders criteria where at least one answer
          is tagged so we don't pad the panel with empty rows. */}
      {finalReport.criterion_breakdown && finalReport.criterion_breakdown.some((c) => c.answers_in_criterion > 0) && (
        <CriteriaSection>
          <SectionHeader>
            <Target size={20} />
            Score by Criterion
          </SectionHeader>
          <CriteriaList>
            {finalReport.criterion_breakdown
              .filter((c) => c.answers_in_criterion > 0)
              .map((c, i) => (
                <CriteriaItem key={`${c.criterion}-${i}`} priorityLevel="Should">
                  <CriteriaHeader>
                    <CriteriaName>{c.criterion}</CriteriaName>
                    <CriteriaWeight hasWeight={!!c.weight}>
                      {typeof c.average_score === 'number'
                        ? `${c.average_score.toFixed(1)} avg`
                        : '—'}
                    </CriteriaWeight>
                  </CriteriaHeader>
                  <CriteriaDescription>
                    {c.answers_in_criterion} answer{c.answers_in_criterion === 1 ? '' : 's'}
                    {c.weight ? ` · ${c.weight}% weight` : ''}
                    {typeof c.weighted_contribution === 'number'
                      ? ` · contributes ${c.weighted_contribution.toFixed(1)} pts`
                      : ''}
                  </CriteriaDescription>
                </CriteriaItem>
              ))}
          </CriteriaList>
        </CriteriaSection>
      )}

      {/* Phase B — UK Cabinet Office PPN-035 disclosure. The text is suitable
          for inclusion in the bidder's cover letter — they can copy it
          verbatim. The model id surfaces here as the auditable record of
          which AI produced the auto-drafted content. */}
      {finalReport.ai_disclosure && (
        <CriteriaSection>
          <SectionHeader>
            <ClipboardCheck size={20} />
            AI Use Disclosure (PPN-035)
          </SectionHeader>
          <pre
            style={{
              margin: 0,
              padding: theme.spacing[3],
              background: theme.colors.backgroundSecondary,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.md,
              fontFamily: theme.typography.fontFamily.mono,
              fontSize: theme.typography.fontSize.xs,
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              color: theme.colors.textPrimary,
            }}
          >
            {finalReport.ai_disclosure.disclosure_text}
          </pre>
          <div
            style={{
              marginTop: theme.spacing[2],
              fontSize: theme.typography.fontSize.xs,
              color: theme.colors.textMuted,
            }}
          >
            Generated {new Date(finalReport.ai_disclosure.generated_at).toLocaleString()} ·
            Model: <code>{finalReport.ai_disclosure.model_id}</code>
          </div>
        </CriteriaSection>
      )}

      <NavigationSection>
        <SectionHeader>
          <ArrowRight size={20} />
          Quick Navigation
        </SectionHeader>

        <NavButton onClick={() => onNavigateToSection?.('criteria')}>
          <NavButtonText>
            <CheckCircle size={16} />
            Evaluation Criteria
          </NavButtonText>
          <ArrowRight size={16} />
        </NavButton>

        <NavButton onClick={() => onNavigateToSection?.('justification')}>
          <NavButtonText>
            <FileText size={16} />
            Submission Justification
          </NavButtonText>
          <ArrowRight size={16} />
        </NavButton>

        <NavButton onClick={() => onNavigateToSection?.('responses')}>
          <NavButtonText>
            <BarChart3 size={16} />
            Refined Responses
          </NavButtonText>
          <ArrowRight size={16} />
        </NavButton>

        {(finalReport.drafted_responses?.length || 0) > 0 && (
          <NavButton onClick={() => onNavigateToSection?.('drafted')}>
            <NavButtonText>
              <FileText size={16} />
              Drafted Responses ({finalReport.drafted_responses?.length})
            </NavButtonText>
            <ArrowRight size={16} />
          </NavButton>
        )}

        {(finalReport.compliance_items?.length || 0) > 0 && (
          <NavButton onClick={() => onNavigateToSection?.('compliance')}>
            <NavButtonText>
              <ClipboardCheck size={16} />
              Compliance ({finalReport.compliance_items?.length})
            </NavButtonText>
            <ArrowRight size={16} />
          </NavButton>
        )}
      </NavigationSection>
    </SummaryContainer>
  );
};

export default FinalReportSummary;